// src/hooks/useUserStats.js
// Fetches user stats from /api/user-stats (database-backed).

import { useState, useEffect, useCallback } from 'react';
import { fetchWithRetry } from '../utils/api';

export function useUserStats(userId) {
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Bumping this re-runs the effect below with the same userId, so a
  // "Retry" button has something to call after fetchWithRetry's own
  // 3 attempts are all exhausted (see DashboardScreen's error placeholder).
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let cancelled = false;

    // No authenticated user yet — don't fetch (and don't fall back to a
    // shared placeholder id, which would leak one account's stats into
    // another's dashboard).
    if (!userId) {
      setUserStats(null);
      setLoading(false);
      setError(null);
      return undefined;
    }

    async function fetchStats() {
      try {
        setLoading(true);
        // A transient network blip or Neon cold-start (same class of issue
        // fixed for /api/submit-quiz — see fetchWithRetry) used to read as
        // "this account has zero quizzes" on the Dashboard, since a failed
        // fetch left userStats null just like a genuinely brand-new
        // account. Retrying here means one bad response doesn't get
        // mistaken for real data.
        // cache: 'no-store' bypasses the WebView/browser HTTP cache outright
        // (unlike setting a 'Cache-Control' request header, which is just
        // metadata to the server and doesn't change fetch's own caching
        // behavior) -- belt-and-suspenders alongside the no-store response
        // header server.js now sends on this route.
        const res = await fetchWithRetry(`/api/user-stats?user_id=${userId}`, { cache: 'no-store' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) {
          setUserStats(data);
          setError(null);
        }
      } catch (err) {
        console.warn('[useUserStats] fetch error:', err);
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchStats();
    return () => { cancelled = true; };
  }, [userId, retryCount]);

  const refetch = useCallback(() => setRetryCount((c) => c + 1), []);

  return { userStats, loading, error, refetch };
}
